import { faGlobe, faServer, faTrophy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { KillRace } from '../types'
import { formatDate, formatTime, formatUsd, resolvePhase, secondsUntil } from '../utils/killrace'
import { Countdown } from './Countdown'
import { Chip, GoArrow, Panel, PhaseBadge, PRIZE_GREEN, Skeleton } from './KillRaceUI'

const stopwatch = new URL('../assets/stopwatch_icon.svg', import.meta.url).href

/* Bundled with the page rather than read from /media: the two media files
 * these pointed at were not there, so every race without its own banner drew
 * a broken image. A race with an uploaded banner shows that instead. */
const DEFAULT_BANNERS = [new URL('../assets/killrace_default_banner.jpg', import.meta.url).href]

export const KillRaceCard = ({
    race,
    index = 0,
    featured = false,
}: {
    race: KillRace
    /** The only race in its bucket: takes the whole row, on a wider banner so it does not balloon. */
    featured?: boolean
    index?: number
}) => {
    const phase = resolvePhase(race.phase, race.begin, race.end)
    const isLive = phase === 'Live'
    const servers = race.servers ?? []

    // Live races count down to their end, upcoming ones to their start.
    const seconds = secondsUntil(isLive ? race.end : race.begin)
    const banner = race.banner || DEFAULT_BANNERS[index % DEFAULT_BANNERS.length]

    return (
        <Link
            to={`/kill-race/info/${race.uuid}`}
            className='group block focus:outline-none focus-visible:opacity-80'
            aria-label={`${race.name}, ${phase}`}
        >
            <Panel
                className='flex flex-col'
                border={isLive ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.10)'}
            >
                <div className='flex items-center justify-between gap-3 border-b border-white/10 px-4 py-2.5'>
                    {/* Two lines rather than an ellipsis: names like "$500 - Top 25 [OPEN]" want
                        198px in the 185px a phone leaves beside the phase badge, and the part that
                        got cut was the qualifier that distinguishes one race from another. */}
                    <h3 className='line-clamp-2 min-w-0 text-xs font-black uppercase leading-tight tracking-[0.18em] text-white/85'>
                        {race.name}
                    </h3>
                    <PhaseBadge phase={phase} className='shrink-0' />
                </div>

                {/* A featured card is as wide as the page, and 16:7 of 1368px is a 598px banner —
                    so the ratio widens with it rather than the artwork swallowing the fold. */}
                <div
                    className={twMerge(
                        'relative aspect-[16/7] w-full overflow-hidden',
                        featured && 'lg:aspect-[24/7]'
                    )}
                >
                    <img
                        src={banner}
                        alt=''
                        className={twMerge(
                            'h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]',
                            // Dim the artwork, never the numbers — the old card
                            // faded the whole tile to 25% and made upcoming
                            // races unreadable.
                            !isLive && 'brightness-[0.45] saturate-50'
                        )}
                    />
                    <div className='absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/25' />

                    <div className='absolute inset-x-0 top-0 flex flex-wrap justify-end gap-1.5 p-3'>
                        <Chip icon={servers.length > 0 ? faServer : faGlobe}>{race.mode || 'Global'}</Chip>
                        {servers.slice(0, 2).map((server) => (
                            <Chip key={server}>{server}</Chip>
                        ))}
                        {servers.length > 2 && <Chip>+{servers.length - 2}</Chip>}
                    </div>

                    <div className='absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4'>
                        <div className='flex flex-col gap-1'>
                            <span className='text-2xs uppercase leading-none tracking-[0.2em] text-white/55'>
                                Prize pool
                            </span>
                            <span
                                className='text-3xl font-black leading-none tabular-nums md:text-4xl'
                                style={{ color: PRIZE_GREEN, textShadow: '0 0 24px rgba(0,255,144,0.35)' }}
                            >
                                {formatUsd(race.prize)}
                            </span>
                        </div>
                        <div className='flex shrink-0 flex-col items-end gap-1'>
                            <span className='text-2xs uppercase leading-none tracking-[0.2em] text-white/55'>
                                Paid places
                            </span>
                            <span className='flex items-center gap-2 text-xl font-black leading-none'>
                                <FontAwesomeIcon icon={faTrophy} className='text-sm text-white/50' />
                                Top {race.ladder_size}
                            </span>
                        </div>
                    </div>
                </div>

                <div className='flex items-center justify-between gap-4 border-t border-white/10 bg-white/[0.03] px-4 py-3'>
                    <div className='flex min-w-0 items-center gap-2.5'>
                        <img src={stopwatch} alt='' className='h-4 w-4 shrink-0 opacity-60' />
                        <div className='flex min-w-0 flex-col gap-1'>
                            <span className='text-2xs uppercase leading-none tracking-[0.2em] text-white/40'>
                                {isLive ? 'Ends in' : 'Starts in'}
                            </span>
                            <span className='truncate font-black leading-none tabular-nums'>
                                {seconds === null ? '—' : <Countdown seconds={seconds} />}
                            </span>
                        </div>
                    </div>
                    {/* The word is hidden on a phone, not the arrow. "6 days 02:05:57" beside
                        "LEADERBOARD →" wants 152px in a 115px slot, and the countdown is the part
                        worth keeping — the whole card is the link either way. */}
                    <GoArrow className='shrink-0'>
                        <span className='hidden sm:inline'>{isLive ? 'Leaderboard' : 'Details'}</span>
                    </GoArrow>
                </div>

                {/* The exact window, for people who plan around it. Hidden on a phone: the footer
                    above already answers "when" as a countdown, and repeating it as a date range
                    cost a band on every card of a page that was five screens long. The detail page
                    prints it at every width. */}
                <div className='hidden border-t border-white/[0.07] px-4 py-2 text-2xs uppercase tracking-[0.15em] text-white/30 md:block'>
                    {formatDate(race.begin)} {formatTime(race.begin)} to {formatDate(race.end)} {formatTime(race.end)}
                </div>
            </Panel>
        </Link>
    )
}

export const KillRaceCardSkeleton = () => (
    <Panel className='flex flex-col'>
        <div className='flex items-center justify-between gap-3 border-b border-white/10 px-4 py-2.5'>
            <Skeleton className='h-3 w-40' />
            <Skeleton className='h-4 w-16' />
        </div>
        <Skeleton className='aspect-[16/7] w-full' />
        <div className='flex items-center justify-between gap-4 border-t border-white/10 px-4 py-4'>
            <Skeleton className='h-5 w-32' />
            <Skeleton className='h-5 w-24' />
        </div>
    </Panel>
)
